import { useEffect, useMemo, useRef, useState } from 'react';
import GemImage from '../components/GemImage.jsx';
import {
  ALL_GEM_SETS,
  MAIN_STAT_OPTIONS,
  PRIORITY_GOALS,
  SHAPES,
  SUB_STAT_OPTIONS,
  nextInstanceNo,
} from '../lib/gemLogic.js';
import { elementLabel, monsterDisplayName } from '../lib/astro.js';

function runRecommendation(payload) {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('../workers/recommend.worker.js', import.meta.url), { type: 'module' });
    worker.onmessage = (event) => {
      worker.terminate();
      if (event.data?.ok) resolve(event.data.output);
      else reject(new Error(event.data?.error || 'Recommendation failed'));
    };
    worker.onerror = (event) => {
      worker.terminate();
      reject(new Error(event.message || 'Worker failed'));
    };
    worker.postMessage(payload);
  });
}

const RESULT_DISPLAY_OPTIONS = [
  { value: 'stats', ko: '수치만', en: 'Stats only' },
  { value: 'images', ko: '사진만', en: 'Images only' },
  { value: 'both', ko: '수치 + 사진', en: 'Stats + images' },
];

const STAT_LABELS = {
  ko: {
    hp: '체력',
    atk: '공격력',
    def: '방어력',
    rec: '회복력',
    crate: '치명확률',
    cdmg: '치명타 피해',
    res: '효과저항',
    acc: '효과적중',
  },
  en: {
    hp: 'HP',
    atk: 'ATK',
    def: 'DEF',
    rec: 'REC',
    crate: 'Crit Rate',
    cdmg: 'Crit Damage',
    res: 'Resistance',
    acc: 'Accuracy',
  },
};

function n(value, digits = 1) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatSetBonus(result, lang) {
  if (!result?.activeSet) return '';
  const p = result.set_bonus_pct || {};
  const f = result.set_bonus_flat || {};
  const labels = STAT_LABELS[lang] || STAT_LABELS.ko;
  const parts = [];

  for (const key of ['hp', 'atk', 'def', 'rec']) {
    if (Number(f[key] || 0) !== 0) parts.push(`${labels[key]} +${n(f[key], 0)}`);
    if (Number(p[key] || 0) !== 0) parts.push(`${labels[key]} +${n(p[key])}%`);
  }
  for (const key of ['crate', 'cdmg', 'res', 'acc']) {
    if (Number(p[key] || 0) !== 0) parts.push(`${labels[key]} +${n(p[key])}%`);
  }
  return parts.join(' · ');
}

function ResultStatSummary({ result, monster, base, lang }) {
  const p = result.tot_pct || {};
  const f = result.tot_flat || {};
  const ko = lang !== 'en';

  const final = {
    hp: Number(base?.hp || 0) + Number(result.hp || 0),
    atk: Number(base?.atk || 0) + Number(result.atk || 0),
    def: Number(base?.def || 0) + Number(result.def || 0),
    rec: Number(base?.rec || 0) + Number(result.rec || 0),
    crate: Number(monster?.crit_rate ?? 10) + Number(p.crate || 0),
    cdmg: Number(monster?.crit_dmg ?? 150) + Number(p.cdmg || 0),
    res: Number(monster?.resist ?? 0) + Number(p.res || 0),
    acc: Number(monster?.accuracy ?? 0) + Number(p.acc || 0),
  };

  return (
    <div className="legacy-stat-summary-wrap">
      <div className="result-summary-title">
        {ko ? '📊 스탯 상승 종합 (세트 효과 포함)' : '📊 Total stat increase (set bonus included)'}
      </div>
      {result.activeSet ? (
        <div className="set-bonus-breakdown">
          <b>{ko ? `✨ 발동된 세트 효과: ${result.activeSet}` : `✨ Active set effect: ${result.activeSet}`}</b>
          {formatSetBonus(result, lang) && <span>{formatSetBonus(result, lang)}</span>}
        </div>
      ) : (
        <div className="muted legacy-no-set">{ko ? 'ℹ️ 발동된 세트 효과 없음 (동일 세트 보석 3개 필요)' : 'ℹ️ No active set effect (3 matching set gems required)'}</div>
      )}

      <div className="legacy-stat-summary">
        <div className="legacy-stat-column">
          <h4>{ko ? '[ 깡(+)옵션 합계 / 퍼센트(%) 합계 ]' : '[ Flat (+) total / Percent (%) total ]'}</h4>
          <div className="legacy-stat-row"><span>{ko ? '체력' : 'HP'}:</span><b>+{n(f.hp, 0)} / +{n(p.hp)}%</b></div>
          <div className="legacy-stat-row"><span>{ko ? '공격력' : 'ATK'}:</span><b>+{n(f.atk, 0)} / +{n(p.atk)}%</b></div>
          <div className="legacy-stat-row"><span>{ko ? '방어력' : 'DEF'}:</span><b>+{n(f.def, 0)} / +{n(p.def)}%</b></div>
          <div className="legacy-stat-row"><span>{ko ? '회복력' : 'REC'}:</span><b>+{n(f.rec, 0)} / +{n(p.rec)}%</b></div>
          <div className="legacy-stat-row"><span>{ko ? '치명확률 / 치명피해' : 'Crit Rate / Crit Damage'}:</span><b>+{n(p.crate)}% / +{n(p.cdmg)}%</b></div>
          <div className="legacy-stat-row"><span>{ko ? '효과저항 / 효과적중' : 'Resistance / Accuracy'}:</span><b>+{n(p.res)}% / +{n(p.acc)}%</b></div>
        </div>

        <div className="legacy-stat-column">
          <h4>{ko ? '[ 최종 스탯 증가량 ]' : '[ Final stat increase ]'}</h4>
          <div className="legacy-stat-row"><span>{ko ? '최종 체력 증가량' : 'HP increase'}:</span><b>+{n(result.hp, 0)}</b></div>
          <div className="legacy-stat-row"><span>{ko ? '최종 공격력 증가량' : 'ATK increase'}:</span><b>+{n(result.atk, 0)}</b></div>
          <div className="legacy-stat-row"><span>{ko ? '최종 방어력 증가량' : 'DEF increase'}:</span><b>+{n(result.def, 0)}</b></div>
          <div className="legacy-stat-row"><span>{ko ? '최종 회복력 증가량' : 'REC increase'}:</span><b>+{n(result.rec, 0)}</b></div>
        </div>

        <div className="legacy-stat-column">
          <h4>{ko ? '[ 적용된 최종 스탯 ]' : '[ Applied final stats ]'}</h4>
          <div className="legacy-stat-row"><span>{ko ? '최종 체력' : 'Final HP'}:</span><b>{n(final.hp, 0)}</b></div>
          <div className="legacy-stat-row"><span>{ko ? '최종 공격력' : 'Final ATK'}:</span><b>{n(final.atk, 0)}</b></div>
          <div className="legacy-stat-row"><span>{ko ? '최종 방어력' : 'Final DEF'}:</span><b>{n(final.def, 0)}</b></div>
          <div className="legacy-stat-row"><span>{ko ? '최종 회복력' : 'Final REC'}:</span><b>{n(final.rec, 0)}</b></div>
          <div className="legacy-stat-row"><span>{ko ? '치명확률 / 치명피해' : 'Crit Rate / Crit Damage'}:</span><b>{n(final.crate)}% / {n(final.cdmg)}%</b></div>
          <div className="legacy-stat-row"><span>{ko ? '효과저항 / 효과적중' : 'Resistance / Accuracy'}:</span><b>{n(final.res)}% / {n(final.acc)}%</b></div>
        </div>
      </div>
    </div>
  );
}

function GemResultCard({ item, index, imageSource, displayMode, lang }) {
  const showImage = displayMode !== 'stats';
  const showStats = displayMode !== 'images';
  const gem = item.gem;
  const star = gem.star ?? gem.stars ?? 6;
  const subStats = (gem.sub_stats || []).filter((x) => x && typeof x === 'object');
  const ko = lang !== 'en';

  return (
    <div className={`gem-card legacy-gem-card result-display-${displayMode}`}>
      <div className="legacy-slot-banner">
        {ko ? `슬롯 ${index + 1}` : `Slot ${index + 1}`}: {gem.name || (ko ? '보석' : 'Gem')} (#{gem.id})
      </div>

      {showStats && (
        <div className="legacy-gem-text">
          <div>• {ko ? '모양' : 'Shape'}: <b>{item.shape || gem.shape || '-'}</b> | {ko ? '강화' : 'Enhance'}: <b>+{gem.enhancement ?? 0}{ko ? '강' : ''}</b> | {ko ? '별' : 'Star'}: <b>★{star}</b></div>
          <div>• {ko ? '세트' : 'Set'}: <b>{gem.set_option || '-'}</b></div>
          <div>• <b>{ko ? '주옵션' : 'Main'}</b>: {gem.main_stat?.name || '-'} {gem.main_stat?.value || ''}</div>
          <div className="legacy-substats"><b>{ko ? '부옵션' : 'Substats'}:</b>
            {subStats.length ? subStats.map((x, i) => <span key={i}>• {x.name} {x.value}</span>) : <span>• -</span>}
          </div>
        </div>
      )}

      {showImage && <GemImage fileName={gem.file_name} imageSource={imageSource} alt={gem.name} />}
    </div>
  );
}

export default function RecommendPage({ gems, astro, onChange, imageSource, lang, t, setMessage }) {
  const normalGems = useMemo(() => gems.filter((g) => !['보류', '휴지통'].includes(g.status)), [gems]);
  const [query, setQuery] = useState('');
  const [selectedUid, setSelectedUid] = useState(astro[0]?.uid ?? '');
  const [base, setBase] = useState({ hp: astro[0]?.hp || 30000, atk: astro[0]?.attack || 3000, def: astro[0]?.defence || 2500, rec: astro[0]?.recovery || 2000 });
  const [slots, setSlots] = useState([
    { shape: '네모', main: '상관없음' },
    { shape: '세모', main: '상관없음' },
    { shape: '마름모', main: '상관없음' },
  ]);
  const [targetSet, setTargetSet] = useState(ALL_GEM_SETS[0]);
  const [priorityGoal, setPriorityGoal] = useState(PRIORITY_GOALS[0]);
  const [resultDisplay, setResultDisplay] = useState('both');
  const [subFilters, setSubFilters] = useState([]);
  const [includeAllEquipped, setIncludeAllEquipped] = useState(false);
  const [includeSame, setIncludeSame] = useState(false);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const runId = useRef(0);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return astro.slice(0, 250);
    return astro.filter((m) => [m.name_kor, m.name, m.element, m.stat_type].some((x) => String(x || '').toLowerCase().includes(q))).slice(0, 250);
  }, [astro, query]);

  const selectedMonster = astro.find((m) => String(m.uid) === String(selectedUid)) || matches[0] || astro[0] || null;
  const displayMatches = useMemo(() => {
    if (!selectedMonster || matches.some((m) => String(m.uid) === String(selectedMonster.uid))) return matches;
    return [selectedMonster, ...matches];
  }, [matches, selectedMonster]);
  const currentName = monsterDisplayName(selectedMonster, 'ko');

  useEffect(() => {
    if (!astro.length || selectedUid) return;
    const first = astro[0];
    setSelectedUid(first.uid);
    setBase({ hp: first.hp || 30000, atk: first.attack || 3000, def: first.defence || 2500, rec: first.recovery || 2000 });
  }, [astro, selectedUid]);

  function selectMonster(uid) {
    setSelectedUid(uid);
    const m = astro.find((x) => String(x.uid) === String(uid));
    if (m) setBase({ hp: m.hp || 30000, atk: m.attack || 3000, def: m.defence || 2500, rec: m.recovery || 2000 });
  }

  async function run() {
    if (!selectedMonster) return;
    const id = ++runId.current;
    setRunning(true);
    try {
      const output = await runRecommendation({
        normalGems,
        base,
        slotSettings: slots,
        targetSet,
        priorityGoal,
        subFilters,
        includeAllEquipped,
        includeSameStarmonGems: includeSame,
        currentMonName: currentName,
      });
      if (id === runId.current) setResult(output);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      if (id === runId.current) setRunning(false);
    }
  }

  function saveCombo(combo) {
    if (!selectedMonster) return;
    const nextNo = nextInstanceNo(gems, currentName);
    const label = `${currentName} (${nextNo})`;
    const ids = new Set(combo.map((x) => x.gem.id));
    const variantKey = `uid:${selectedMonster.uid}`;
    onChange(gems.map((g) => ids.has(g.id) ? {
      ...g,
      equipped_to: label,
      equipped_monster_uid: selectedMonster.uid,
      equipped_variant_key: variantKey,
      equipped_element: selectedMonster.element,
      equipped_evolution: selectedMonster.evolution,
      equipped_stat_type: selectedMonster.stat_type,
      equipped_monster_name_kor: currentName,
      equipped_instance_no: nextNo,
    } : g));
    setMessage({ type: 'success', text: `[${label}]에 ${ids.size}개 보석을 장착 저장했습니다.` });
  }

  const baseLabels = lang === 'en'
    ? { hp: 'HP', atk: 'ATK', def: 'DEF', rec: 'REC' }
    : { hp: '체력', atk: '공격력', def: '방어력', rec: '회복력' };

  return (
    <section className="page-section">
      <div className="section-heading"><div><h2>⚔️ {t('recommend')}</h2><p>진화 3/4단계 Astroguide 기반</p></div></div>

      <div className="card">
        <h3>1. {t('chooseMonster')}</h3>
        <div className="filters-row">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="아서 / Arthur / fire..." />
          <select value={selectedMonster?.uid ?? ''} onChange={(e) => selectMonster(e.target.value)}>
            {displayMatches.map((m) => <option key={m.uid} value={m.uid}>[{elementLabel(m.element, lang)}] {monsterDisplayName(m, lang)} · Evo {m.evolution} · {m.stat_type}</option>)}
          </select>
        </div>
        {selectedMonster && (
          <div className="monster-summary">
            <div className="monster-avatar">🐾</div>
            <div><b>{monsterDisplayName(selectedMonster, lang)}</b><div className="muted">{elementLabel(selectedMonster.element, lang)} · Evo {selectedMonster.evolution} · {selectedMonster.stat_type} · UID {selectedMonster.uid}</div></div>
          </div>
        )}
        <div className="form-grid four">
          {['hp', 'atk', 'def', 'rec'].map((k) => <label key={k}>{baseLabels[k]}<input type="number" value={base[k]} onChange={(e) => setBase({ ...base, [k]: Number(e.target.value) })} /></label>)}
        </div>
      </div>

      <div className="card">
        <h3>2. 슬롯 조건</h3>
        <div className="slot-grid">
          {slots.map((slot, i) => <div className="slot-card" key={i}>
            <b>슬롯 {i + 1}</b>
            <select value={slot.shape} onChange={(e) => setSlots(slots.map((s, idx) => idx === i ? { ...s, shape: e.target.value } : s))}>{SHAPES.map((x) => <option key={x}>{x}</option>)}</select>
            <select value={slot.main} onChange={(e) => setSlots(slots.map((s, idx) => idx === i ? { ...s, main: e.target.value } : s))}>{MAIN_STAT_OPTIONS.map((x) => <option key={x}>{x}</option>)}</select>
          </div>)}
        </div>
      </div>

      <div className="card">
        <h3>3. {lang === 'en' ? 'Set / Goal / Result display' : '세트 / 목표 / 결과 표시 설정'}</h3>
        <div className="form-grid three">
          <label>{lang === 'en' ? 'Set' : '세트'}<select value={targetSet} onChange={(e) => setTargetSet(e.target.value)}>{ALL_GEM_SETS.map((x) => <option key={x}>{x}</option>)}</select></label>
          <label>{lang === 'en' ? 'Priority goal' : '우선 목표'}<select value={priorityGoal} onChange={(e) => setPriorityGoal(e.target.value)}>{PRIORITY_GOALS.map((x) => <option key={x}>{x}</option>)}</select></label>
          <label>{lang === 'en' ? 'Result display' : '결과 표시'}
            <select value={resultDisplay} onChange={(e) => setResultDisplay(e.target.value)}>
              {RESULT_DISPLAY_OPTIONS.map((x) => <option key={x.value} value={x.value}>{lang === 'en' ? x.en : x.ko}</option>)}
            </select>
          </label>
        </div>
        <div className="inline-heading"><h4>부옵션 조건</h4><button className="small secondary" onClick={() => setSubFilters([...subFilters, { stat: '공격력(%)', val: 10, cond: '이상' }])}>+ 조건 추가</button></div>
        {subFilters.map((f, i) => <div className="subfilter-row" key={i}>
          <select value={f.stat} onChange={(e) => setSubFilters(subFilters.map((x, idx) => idx === i ? { ...x, stat: e.target.value } : x))}>{SUB_STAT_OPTIONS.map((x) => <option key={x}>{x}</option>)}</select>
          <input type="number" step="0.1" value={f.val} onChange={(e) => setSubFilters(subFilters.map((x, idx) => idx === i ? { ...x, val: Number(e.target.value) } : x))} />
          <select value={f.cond} onChange={(e) => setSubFilters(subFilters.map((x, idx) => idx === i ? { ...x, cond: e.target.value } : x))}><option>이상</option><option>이하</option></select>
          <button className="small danger" onClick={() => setSubFilters(subFilters.filter((_, idx) => idx !== i))}>×</button>
        </div>)}
        <div className="checkbox-grid">
          <label><input type="checkbox" checked={includeAllEquipped} onChange={(e) => setIncludeAllEquipped(e.target.checked)} /> 이전에 장착한 모든 보석 포함</label>
          <label><input type="checkbox" checked={includeSame} onChange={(e) => setIncludeSame(e.target.checked)} /> 같은 스타몬이 장착한 보석 포함</label>
        </div>
        <button className="primary-wide" onClick={run} disabled={running}>{running ? '계산 중…' : `🚀 ${t('runRecommend')}`}</button>
      </div>

      {result && (
        <div className="results-block">
          <div className={`search-meta ${result.meta.exact ? 'success' : 'warning'}`}>
            <b>{result.meta.exact ? `✅ ${t('exactSearch')}` : `⚡ ${t('optimizedSearch')}`}</b>
            <span>후보: {result.meta.originalCounts?.join(' × ')} · 검사: {result.meta.searchedCombinations.toLocaleString()}</span>
            {!result.meta.exact && <small>원래 조합 수가 너무 커 브라우저 정지를 막기 위해 슬롯별 유력 후보와 세트/필터 후보를 보존해 축소 탐색했습니다.</small>}
          </div>
          {!result.results.length ? <div className="empty-card">조건을 만족하는 3개 조합이 없습니다.</div> : result.results.map((res, rank) => (
            <div className="card result-card" key={rank}>
              <div className="section-heading"><h3>🏆 {rank + 1}위 · {res.score.toLocaleString(undefined, { maximumFractionDigits: 1 })}점</h3><button onClick={() => saveCombo(res.combo)}>💾 {t('saveCombo')}</button></div>
              {resultDisplay !== 'images' && <ResultStatSummary result={res} monster={selectedMonster} base={base} lang={lang} />}
              <div className="result-gems">
                {res.combo.map((item, i) => <GemResultCard key={item.gem.id} item={item} index={i} imageSource={imageSource} displayMode={resultDisplay} lang={lang} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
